import { FlexColumn, SpaceBetweenRow } from '@fox/layout/src/flex';
import { Padded } from '@fox/layout/src/style';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { createContext, Suspense, useContext, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import styled from 'styled-components';
import { LoadingDotsCenter } from '../animated-foxes/index';
import { ContainerHeightContext, useMeasureRef } from '../container-context/index';
import { Divider } from '../divider/index';
import { Drawer, DrawerProps } from '../drawer/index';
import { ErrorBoundaryWithReset } from '../error-boundary';
import { IconButton } from '../icon-button/index';
import { ClickToDismissPortalContext, ContainerContext } from '../popover/context';
import { useClickToDismiss, useContainer, usePopoverContext } from '../popover/hooks';
import { MuiTypographyProps, Typography } from '../typography/index';
import { Z } from '../z-index/index';

const DrawerRoot = styled.div`
	flex-grow: 1;
	display: flex;
	max-width: 100%;
`;

const DrawerInner = styled.div<{ $isMobile: boolean }>`
	min-height: 100%;
	flex-grow: 1;
	max-width: 100%;
	${({ $isMobile }) =>
		$isMobile &&
		`
		overflow-y: auto;
		max-height: calc(100vh - 200px);
	`}
`;

type EventType = React.MouseEvent<HTMLDivElement | HTMLButtonElement>;
interface IResponsiveDrawerHeaderProps {
	title?: React.ReactNode;
	description?: React.ReactNode;
	onClose?: (e: EventType) => void;
	hideClose?: boolean;
	headerVariant?: MuiTypographyProps['variant'];
	descriptionVariant?: MuiTypographyProps['variant'];
	descriptionTextColor?: MuiTypographyProps['color'];
}

// temp stuff

// this code was brought across from seeourm-core, but because the drawer was from ant it had some extra props
// that don't appear to be implemented. if we end up using ResponsiveDrawer we should implement these
type IDrawerTempProps = {
	closable?: boolean;
	visible?: boolean;
	destroyOnClose?: boolean;
	title?: React.ReactNode;
	description?: React.ReactNode;
	onClose?: (e?: EventType) => void;
	/** flag that due to dirty state we want to remain open. this is important so we can detect the open -> false flag changes */
	onKeepOpen?: () => void;
	/** Number value to use as percent */
	maxWidthPercent?: number;
	forceZIndex?: number;
};

const ResponsiveDrawerHeader = (props: IResponsiveDrawerHeaderProps) => {
	return (
		<FlexColumn>
			<SpaceBetweenRow>
				{props.title && (
					<>
						<Padded>
							<Typography variant={props.headerVariant ?? 'h3'}>{props.title}</Typography>
						</Padded>
					</>
				)}
				{!props.hideClose && (
					<IconButton
						id="drawerClose"
						style={
							// When a title is present title/close-button are center-aligned in row
							// otherwise title hidden and close button is positioned absolutely to top right of drawer
							props.title
								? { fontSize: '1.5em', marginRight: '0.5rem' }
								: { fontSize: '1.5em', position: 'absolute', top: '1rem', zIndex: 999999999999, right: '1rem' }
						}
						iconColor="primary"
						iconName="Clear"
						onClick={(e: any) => props.onClose && props.onClose(e)}
					/>
				)}
			</SpaceBetweenRow>
			{props.description ? (
				<div style={{ padding: '0 1rem 1rem' }}>
					<Typography variant={props.descriptionVariant} color={props.descriptionTextColor}>
						{props.description}
					</Typography>
				</div>
			) : null}
			{props.title || props.description ? <Divider /> : null}
		</FlexColumn>
	);
};
interface IResponsiveDrawerState {
	drawerWidth: string;
	visible?: boolean;
}
const DefaultResponsiveCutOff = 1024;
const Default50CutOff = 1920;

const Context = createContext<undefined | { close: (force?: boolean) => void; lockDrawer?: (lock: boolean) => void }>(undefined);
export const useResponsiveDrawerContext = () => useContext(Context);
export type ResponsiveDrawerProps = Omit<DrawerProps, 'title' | 'onClose'> &
	IDrawerTempProps & {
		headerVariant?: MuiTypographyProps['variant'];
		descriptionVariant?: MuiTypographyProps['variant'];
		descriptionTextColor?: MuiTypographyProps['color'];
	};
/** A drawer that will pin to the bottom when on smaller screens, otherwise flies out from the right on larger screens. The height prop will be used on vertical / mobile mode only.*/
export const ResponsiveDrawer: React.FunctionComponent<ResponsiveDrawerProps> = props => {
	const drawerId = useRef<string>(nanoid());
	const styles = props.style ?? {};

	// If this Drawer is rendered inside of a popover, make it's zIndex = (popper's zIndex + 1)
	const { getContainerRef } = usePopoverContext();
	const popperElementRef = getContainerRef?.();
	if (popperElementRef) {
		const popperZIndex = Number(getComputedStyle(popperElementRef)?.zIndex);
		Object.assign(styles, { zIndex: popperZIndex + 1 });
	}

	const [localState, setLocalState] = useState<IResponsiveDrawerState>({
		drawerWidth: '0%',
		visible: undefined
	});
	const [drawerLocked, setDrawerLocked] = useState(false);
	const responsiveCutOff = props.maxWidthPercent ? DefaultResponsiveCutOff : props.width ? props.width : DefaultResponsiveCutOff;

	const windowResized = React.useCallback(() => {
		let drawerWidth = '33%';
		if (window.innerWidth < Number(responsiveCutOff)) {
			drawerWidth = '100%';
		} else if (props.width) {
			if (window.innerWidth < Number(props.width)) drawerWidth = (props.maxWidthPercent ? props.maxWidthPercent + '%' : undefined) ?? '100%';
			else drawerWidth = props.width + 'px';
		} else {
			if (window.innerWidth <= Default50CutOff) drawerWidth = '50%';
			else {
				drawerWidth = '800px';
			}
		}
		if (localState.drawerWidth !== drawerWidth) {
			setLocalState((ls: any) => {
				return { ...ls, drawerWidth: drawerWidth };
			});
		}
	}, [localState.drawerWidth, props.width, props.maxWidthPercent, responsiveCutOff]);

	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	React.useEffect(() => {
		const handler = () => {
			if (debounceRef.current !== null) clearTimeout(debounceRef.current);
			debounceRef.current = setTimeout(windowResized, 150);
		};

		window.addEventListener('resize', handler);
		windowResized();

		return () => {
			window.removeEventListener('resize', handler);
			if (debounceRef.current !== null) clearTimeout(debounceRef.current);
		};
	}, [windowResized]);

	const { drawerWidth } = localState;

	const isMobile = window.innerWidth < Number(responsiveCutOff);
	const fullSizePlacement = props.placement || 'right';
	const placement = isMobile ? 'bottom' : fullSizePlacement;

	const finalProps = Object.assign({}, props, {
		placement: placement,
		height: isMobile ? props.height : '100%',
		width: drawerWidth,
		title: undefined,
		className: undefined
	});

	// I hate flags where undefined == true value by default!
	let hideClose: boolean | undefined = undefined;
	if (typeof props.closable != 'undefined') {
		hideClose = !props.closable;
	}

	const {
		contextValue,
		shouldAllowClose,
		childrenOpen,
		context: { childPortalClosed, childPortalOpened }
	} = useClickToDismiss();

	// this ref is to work around a race between the dirty state / persistence context allowing the drawer to close
	// and the fact we rely on the DirtyState component to unmount. We know we are explicitly allowing a close, so we remember it
	const overrideChildenOpen = useRef(false);

	useEffect(() => {
		// ensure this flag is reset when the drawer is explicitly requested to be opened
		if (props.open) overrideChildenOpen.current = false;
	}, [props.open]);

	const rerender = useReducer(a => !a, false)[1];

	const closeHandler = (force?: boolean) => {
		if ((shouldAllowClose() || force) && !drawerLocked) {
			overrideChildenOpen.current = true;
			childPortalClosed(drawerId.current);
			props.onClose?.();
			rerender();
			return true;
		}
		return false;
	};

	const lockDrawer = (lock: boolean) => {
		setDrawerLocked(lock);
	};

	// we are open if we are requested to be open, or we have any children open
	const isOpen = props.open || (!overrideChildenOpen.current && childrenOpen());

	useLayoutEffect(() => {
		(isOpen ? childPortalOpened : childPortalClosed)(drawerId.current);
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && !props.open) {
			if (!closeHandler()) {
				// request we remain open
				props.onKeepOpen?.();
			}
		}
	}, [isOpen, props.open]);

	useEffect(() => {
		return () => {
			// we may not have control over our unmounting, so let's at least keep us in sync
			childPortalClosed(drawerId.current, true);
		};
	}, []);

	const { ref: drawerRef, containerSetter } = useContainer();
	const [ref, measure] = useMeasureRef();

	return (
		<ClickToDismissPortalContext.Provider value={contextValue}>
			<Context.Provider
				value={{
					close: closeHandler,
					lockDrawer: lockDrawer
				}}
			>
				<ContainerContext.Provider value={drawerRef.current}>
					<Drawer
						maskClosable={props.maskClosable}
						onClose={() => closeHandler()}
						placement={props.placement ?? placement}
						open={isOpen}
						zIndex={props.forceZIndex ?? Z.drawer}
						width={finalProps.width}
						height={finalProps.height}
						style={{
							pointerEvents: placement === 'bottom' && !props.open ? 'none' : undefined,
							...styles
						}}
						contentWrapperStyle={props.contentWrapperStyle}
					>
						{!props.destroyOnClose || props.open ? (
							<div
								id={drawerId.current}
								ref={containerSetter}
								// drag and drop requires this?
								// onMouseDown={e => e.stopPropagation()}
								onKeyDown={e => e.stopPropagation()}
								style={{
									minHeight: '100%',
									height: finalProps.height,
									display: 'flex',
									flexDirection: 'column'
								}}
							>
								<ResponsiveDrawerHeader
									hideClose={hideClose}
									title={props.title}
									description={props.description}
									onClose={() => closeHandler()}
									headerVariant={props.headerVariant}
									descriptionVariant={props.descriptionVariant}
									descriptionTextColor={props.descriptionTextColor}
								/>
								<ContainerHeightContext measure={measure}>
									<ErrorBoundaryWithReset>
										<DrawerRoot>
											<DrawerInner $isMobile={placement === 'bottom'} ref={ref}>
												<Suspense fallback={<LoadingDotsCenter />}>
													<div className={props.className} style={{ height: '100%', width: '100%' }}>
														{isOpen && props.children}
													</div>
												</Suspense>
											</DrawerInner>
										</DrawerRoot>
									</ErrorBoundaryWithReset>
								</ContainerHeightContext>
							</div>
						) : null}
					</Drawer>
				</ContainerContext.Provider>
			</Context.Provider>
		</ClickToDismissPortalContext.Provider>
	);
};
